<template>
  <div class="program-creation">
    <div class="page-top">
      <button type="button" class="btn-back" @click="goBack">{{ $t('bo.common.back') }}</button>
      <div>
        <h1>{{ pageTitle }}</h1>
        <p>{{ $t('bo.programs.creation.subtitle') }}</p>
      </div>
    </div>

    <div v-if="store.isLoadingProgram" class="state-msg">{{ $t('bo.common.loading') }}</div>

    <template v-else>
      <nav class="stepper" aria-label="Program creation steps">
        <button
          v-for="step in steps"
          :key="step.id"
          type="button"
          class="step-item"
          :class="{ active: currentStep === step.id, done: currentStep > step.id }"
          :disabled="!canGoToStep(step.id)"
          @click="goToStep(step.id)"
        >
          <span class="step-num">{{ step.id }}</span>
          <span class="step-label">{{ step.title }}</span>
        </button>
      </nav>

      <div v-if="store.error" class="error-banner">{{ store.error }}</div>

      <div class="wizard-body">
        <aside class="summary-panel">
          <h3>{{ $t('bo.programs.creation.summary') }}</h3>
          <dl>
            <dt>{{ $t('bo.programs.labelName') }}</dt>
            <dd>{{ store.programName || '—' }}</dd>
            <dt>{{ $t('bo.programs.labelGoal') }}</dt>
            <dd>{{ store.goal || '—' }}</dd>
            <dt>{{ $t('bo.programs.creation.sessionsCount') }}</dt>
            <dd>{{ store.sessions.length }}</dd>
            <dt>{{ $t('bo.programs.creation.resourcesCount') }}</dt>
            <dd>{{ store.resources.length }}</dd>
            <dt>{{ $t('bo.programs.creation.questionsCount') }}</dt>
            <dd>{{ store.questions.length }}</dd>
          </dl>
        </aside>

        <section class="step-content">
          <component :is="activeStepComponent" ref="stepComponentRef" />
        </section>
      </div>

      <div class="wizard-footer">
        <button
          type="button"
          class="btn-secondary"
          :disabled="store.isSubmitting"
          @click="handleSecondary"
        >
          {{ secondaryLabel }}
        </button>
        <button
          type="button"
          class="btn-primary"
          :disabled="store.isSubmitting"
          @click="handlePrimary"
        >
          {{ store.isSubmitting ? $t('bo.common.saving') : primaryLabel }}
        </button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useAdminProgramCreationStore } from '@/stores/adminProgramCreation'
import StepBrief from './steps/StepBrief.vue'
import StepSettings from './steps/StepSettings.vue'
import StepSessions from './steps/StepSessions.vue'
import StepResources from './steps/StepResources.vue'
import StepQuestions from './steps/StepQuestions.vue'
import StepLocalization from './steps/StepLocalization.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const store = useAdminProgramCreationStore()
const { programId, isArchivedProgram } = storeToRefs(store)

const currentStep = ref(1)
const stepComponentRef = ref(null)

const steps = computed(() => [
  { id: 1, title: t('bo.programs.creation.steps.brief'), component: StepBrief },
  { id: 2, title: t('bo.programs.creation.steps.settings'), component: StepSettings },
  { id: 3, title: t('bo.programs.creation.steps.sessions'), component: StepSessions },
  { id: 4, title: t('bo.programs.creation.steps.resources'), component: StepResources },
  { id: 5, title: t('bo.programs.creation.steps.questions'), component: StepQuestions },
  { id: 6, title: t('bo.programs.creation.steps.localization'), component: StepLocalization },
])

const totalSteps = computed(() => steps.value.length)
const isLastStep = computed(() => currentStep.value === totalSteps.value)
const activeStepComponent = computed(() => steps.value[currentStep.value - 1]?.component ?? StepBrief)

const isEditRoute = computed(() => Boolean(route.params.id))
const pageTitle = computed(() =>
  isEditRoute.value ? t('bo.programs.creation.editTitle') : t('bo.programs.creation.createTitle')
)

const primaryLabel = computed(() => {
  if (isArchivedProgram.value) {
    return isLastStep.value ? t('bo.common.close') : t('bo.programs.creation.continue')
  }
  if (!isLastStep.value) return t('bo.programs.creation.continue')
  return store.programStatus === 'active'
    ? t('bo.programs.creation.saveAndClose')
    : t('bo.programs.creation.publishProgram')
})

const secondaryLabel = computed(() => {
  if (isLastStep.value && !isArchivedProgram.value) return t('bo.programs.creation.saveDraft')
  return currentStep.value === 1 ? t('bo.common.cancel') : t('bo.common.previous')
})

const canGoToStep = (stepId) => {
  if (stepId === 1) return true
  return Boolean(programId.value)
}

const goToStep = (stepId) => {
  if (!canGoToStep(stepId)) return
  currentStep.value = stepId
}

const goBack = () => router.push({ name: 'admin.programs' })

const handleSecondary = async () => {
  if (isLastStep.value && !isArchivedProgram.value) {
    goBack()
    return
  }
  if (currentStep.value > 1) {
    currentStep.value -= 1
    return
  }
  goBack()
}

const handlePrimary = async () => {
  if (store.isSubmitting) return

  if (isArchivedProgram.value) {
    if (isLastStep.value) {
      goBack()
      return
    }
    currentStep.value += 1
    return
  }

  try {
    if (currentStep.value === 1) {
      await store.submitStep1()
      if (!isEditRoute.value && store.programId) {
        await router.replace({
          name: 'admin.programs.edit',
          params: { id: store.programId },
        })
      }
    } else if (currentStep.value === 2) {
      await store.submitMatchingType()
    } else if (currentStep.value === 6) {
      await stepComponentRef.value?.saveChangedTranslations?.()
      if (store.programStatus !== 'active') {
        await store.publishProgram()
      }
      goBack()
      return
    }

    if (currentStep.value < totalSteps.value) {
      currentStep.value += 1
    }
  } catch {
    // error shown in store
  }
}

onMounted(async () => {
  store.resetProgram()
  if (route.params.id) {
    await store.loadProgram(route.params.id)
  }
})

onUnmounted(() => {
  store.resetProgram()
})

watch(
  () => route.params.id,
  async (id) => {
    if (id) {
      store.resetProgram()
      await store.loadProgram(id)
    }
  }
)
</script>

<style scoped>
.program-creation {
  padding: 1.5rem 2rem 2rem;
  max-width: 1200px;
}

.page-top {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.page-top h1 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.25rem;
}

.page-top p {
  margin: 0;
  color: #6b7280;
  font-size: 0.875rem;
}

.btn-back {
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  padding: 0.4rem 0.75rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
  margin-top: 0.25rem;
}

.stepper {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 1.25rem;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 9999px;
  background: white;
  cursor: pointer;
  font-size: 0.8rem;
}

.step-item.active {
  border-color: #3b82f6;
  background: #eff6ff;
  color: #1d4ed8;
}

.step-item.done:not(.active) {
  border-color: #bbf7d0;
  background: #f0fdf4;
}

.step-item:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.step-num {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 9999px;
  background: #e5e7eb;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
}

.step-item.active .step-num {
  background: #3b82f6;
  color: white;
}

.wizard-body {
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.25rem;
  align-items: start;
}

.summary-panel {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 1rem;
}

.summary-panel h3 {
  margin: 0 0 0.75rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: #374151;
}

.summary-panel dl {
  margin: 0;
}

.summary-panel dt {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9ca3af;
  margin-top: 0.65rem;
}

.summary-panel dd {
  margin: 0.15rem 0 0;
  font-size: 0.875rem;
  color: #1f2937;
  word-break: break-word;
}

.step-content {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 1.25rem;
  min-height: 320px;
}

.error-banner {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  margin-bottom: 1rem;
}

.wizard-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.25rem;
}

.btn-primary,
.btn-secondary {
  padding: 0.5rem 1.25rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-primary {
  background: #3b82f6;
  border: none;
  color: white;
}

.btn-primary:disabled,
.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: white;
  border: 1px solid #d1d5db;
  color: #374151;
}

.state-msg {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
}

@media (max-width: 900px) {
  .wizard-body {
    grid-template-columns: 1fr;
  }
}
</style>
